/**
 * State persistence service
 *
 * Handles saving/loading state to both localStorage (when offline)
 * and timelines (when authenticated). Integrates with syncManager
 * for offline support and conflict detection.
 */

import timelineService, { scheduleToEntries, entriesToSchedule } from './timelineService';
import syncManager from '../../sync/services/syncManager';
import type { Element, Schedule } from '@thekwallace/backtime-shared';

// ============================================================================
// Types
// ============================================================================

const STORAGE_KEY = 'serviceTimerState';
const LAST_UPDATED_KEY = 'serviceTimerLastUpdated';

/** State data that can be saved/loaded */
export interface TimelineState {
  elements: Element[];
  selectedStartTime: string | null;
  timeOffset: number;
  schedule?: Schedule; // UI uses Schedule format, converted at API boundary
  isAutoAdvanceEnabled?: boolean;
  autoAdvanceBuffer?: number;
}

/** Options for saving to cloud */
export interface SaveOptions {
  /** Whether to track changes for sync (default: true) */
  trackChanges?: boolean;
  /** Specific fields that changed (for change tracking) */
  changedFields?: Record<
    string,
    { oldValue: unknown; newValue: unknown }
  > | null;
}

/** Error with optional code property */
interface ApiError extends Error {
  code?: string;
  serverUpdatedAt?: string;
}

// ============================================================================
// State Service
// ============================================================================

class StateService {
  /** Track the last known server updatedAt timestamp */
  private _lastUpdatedAt: string | null = null;

  /**
   * Set the last known server updatedAt timestamp
   */
  setLastUpdatedAt(updatedAt: string): void {
    this._lastUpdatedAt = updatedAt;
    try {
      localStorage.setItem(LAST_UPDATED_KEY, updatedAt);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Get the last known server updatedAt timestamp
   */
  getLastUpdatedAt(): string | null {
    if (this._lastUpdatedAt) return this._lastUpdatedAt;
    try {
      return localStorage.getItem(LAST_UPDATED_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Save state to timeline
   */
  async saveToCloud(
    timelineId: string,
    state: TimelineState,
    options: SaveOptions = {}
  ): Promise<boolean> {
    if (!timelineId) {
      throw new Error('Timeline ID is required to save to cloud');
    }

    const { trackChanges = true, changedFields = null } = options;

    try {
      const updateData: Record<string, unknown> = {
        elements: state.elements,
        selectedStartTime: state.selectedStartTime,
        timeOffset: state.timeOffset,
      };

      // Include auto-advance settings if present
      if (state.isAutoAdvanceEnabled !== undefined) {
        updateData.isAutoAdvanceEnabled = state.isAutoAdvanceEnabled;
      }
      if (state.autoAdvanceBuffer !== undefined) {
        updateData.autoAdvanceBuffer = state.autoAdvanceBuffer;
      }
      // Include schedule if present (convert to scheduleEntries for API)
      if (state.schedule !== undefined) {
        updateData.scheduleEntries = scheduleToEntries(state.schedule);
      }

      // Track changes for sync if enabled
      if (trackChanges && changedFields) {
        const lastUpdatedAt = this.getLastUpdatedAt();
        Object.keys(changedFields).forEach((field) => {
          syncManager.recordChange({
            timelineId,
            field,
            oldValue: changedFields[field].oldValue,
            newValue: changedFields[field].newValue,
            lastUpdatedAt,
          });
        });
      }

      const lastUpdatedAt = this.getLastUpdatedAt();
      console.log(
        '[stateService] Sending update:',
        JSON.stringify(updateData, null, 2),
        'ifUnmodifiedSince:',
        lastUpdatedAt
      );
      const result = await timelineService.updateTimeline(
        timelineId,
        updateData,
        lastUpdatedAt ? { ifUnmodifiedSince: lastUpdatedAt } : undefined
      );

      // Update the last known server timestamp
      if (result?.updatedAt) {
        this.setLastUpdatedAt(result.updatedAt);
      }

      return true;
    } catch (error) {
      console.error('Error saving to cloud:', error);

      const apiError = error as ApiError;
      // If it's a conflict, the syncManager will handle it
      if (apiError.code === 'CONFLICT') {
        // Update our timestamp to the server's
        if (apiError.serverUpdatedAt) {
          this.setLastUpdatedAt(apiError.serverUpdatedAt);
        }
      }

      throw error;
    }
  }

  /**
   * Load state from timeline
   */
  async loadFromCloud(timelineId: string): Promise<TimelineState | null> {
    if (!timelineId) {
      throw new Error('Timeline ID is required to load from cloud');
    }

    try {
      const timeline = await timelineService.getTimeline(timelineId);

      if (timeline) {
        // Track the server's updatedAt timestamp for conflict detection
        if (timeline.updatedAt) {
          this.setLastUpdatedAt(timeline.updatedAt);
        }

        return {
          elements: timeline.elements,
          selectedStartTime: timeline.selectedStartTime ?? null,
          timeOffset: timeline.timeOffset,
          schedule: entriesToSchedule(timeline.scheduleEntries),
        };
      }

      return null;
    } catch (error) {
      console.error('Error loading from cloud:', error);
      throw error;
    }
  }

  /**
   * Save state to localStorage (fallback when offline or not authenticated)
   */
  saveToLocal(state: TimelineState): boolean {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }

  /**
   * Load state from localStorage
   */
  loadFromLocal(): TimelineState | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear localStorage
   */
  clearLocal(): boolean {
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Save state (automatically chooses cloud or local based on timeline)
   */
  async save(
    timelineId: string | null,
    state: TimelineState
  ): Promise<void> {
    // Always save to local as backup
    this.saveToLocal(state);

    // If timelineId exists, also save to cloud
    if (timelineId) {
      try {
        await this.saveToCloud(timelineId, state);
      } catch (error) {
        console.error(
          'Failed to save to cloud, but local save succeeded:',
          error
        );
      }
    }
  }

  /**
   * Load state (automatically chooses cloud or local based on timeline)
   */
  async load(timelineId: string | null): Promise<TimelineState | null> {
    // If timelineId exists, try to load from cloud first
    if (timelineId) {
      try {
        const cloudState = await this.loadFromCloud(timelineId);
        if (cloudState) {
          // Update local storage with cloud data
          this.saveToLocal(cloudState);
          return cloudState;
        }
      } catch (error) {
        console.error(
          'Failed to load from cloud, falling back to local:',
          error
        );
      }
    }

    // Fall back to local storage
    return this.loadFromLocal();
  }
}

// Export singleton instance
const stateService = new StateService();
export default stateService;

// Export class for testing or custom instances
export { StateService };
